import {useEffect, useState} from "react";
import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Modal,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {CKEditor} from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

import {useAlert} from "../context/AlertContext";
import {createPracticeTask} from "../api/api";
import {CreatePracticeTaskRequest} from "../types";
import AppModalBox from "../components/AppModalBox";

interface Props {
    open: boolean;
    onClose: () => void;
    lessonId: number;
    onCreated: () => void;
}

type EditorMode = "RICH" | "CODE";

const AddPracticeTaskModal = ({open, onClose, lessonId, onCreated}: Props) => {
    const {showAlert} = useAlert();

    const [title, setTitle] = useState("");
    const [orderNum, setOrderNum] = useState<number>(1);
    const [content, setContent] = useState("");
    const [saving, setSaving] = useState(false);
    const [mode, setMode] = useState<EditorMode>("RICH");

    useEffect(() => {
        if (!open) return;

        setTitle("");
        setOrderNum(1);
        setContent("");
        setSaving(false);
        setMode("RICH");
    }, [open]);

    const ckeditorSx = (theme: any) => ({
        mb: 2,

        "& .ck.ck-editor": {
            width: "100%",
        },

        "& .ck.ck-editor__main > .ck-editor__editable": {
            minHeight: "300px",
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
            borderColor: `${theme.palette.divider} !important`,
        },

        "& .ck-content": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.default : "#fff"
            } !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar": {
            backgroundColor: `${
                theme.palette.mode === "dark" ? theme.palette.background.paper : "#f5f5f5"
            } !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-toolbar .ck-button, & .ck.ck-toolbar .ck-button .ck-icon": {
            color: `${theme.palette.text.primary} !important`,
            fill: `${theme.palette.text.primary} !important`,
        },

        "& .ck.ck-dropdown__panel": {
            backgroundColor: `${theme.palette.background.paper} !important`,
            borderColor: `${theme.palette.divider} !important`,
            color: `${theme.palette.text.primary} !important`,
        },
    });

    const codeTextAreaSx = {
        "& .MuiInputBase-input": {
            fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
            whiteSpace: "pre",
            lineHeight: 1.5,
        },
    };

    const submit = async () => {
        if (!lessonId) return;

        const req: CreatePracticeTaskRequest = {
            title: title.trim(),
            content,
            orderNum: Number(orderNum),
        };

        if (!req.title) {
            showAlert("Title is required", "error");
            return;
        }

        if (!req.orderNum || req.orderNum < 1) {
            showAlert("Order must be >= 1", "error");
            return;
        }

        if (!req.content || !req.content.trim()) {
            showAlert("Task content is required", "error");
            return;
        }

        try {
            setSaving(true);
            await createPracticeTask(lessonId, req);
            showAlert("Practice task created successfully", "success");
            onClose();
            onCreated();
        } catch (e: any) {
            showAlert(e?.message ?? "Failed to create practice task", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <AppModalBox
                sx={{
                    p: 4,
                    width: "60%",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    outline: "none",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                }}
            >
                <Typography variant="h6" sx={{mb: 1}}>
                    Add Practice Task
                </Typography>

                <Typography variant="body2" sx={{color: "text.secondary", mb: 2}}>
                    Choose how you want to write content: Rich editor or Pure HTML/CSS code.
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                checked={mode === "CODE"}
                                onChange={(_, checked) => setMode(checked ? "CODE" : "RICH")}
                            />
                        }
                        label={mode === "CODE" ? "Pure Code Mode" : "Rich Editor Mode"}
                    />
                </Box>

                <Divider sx={{mb: 2}} />

                <TextField
                    label="Practice Task Title"
                    fullWidth
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    sx={{mb: 2}}
                />

                <TextField
                    label="Order"
                    type="number"
                    fullWidth
                    required
                    value={orderNum}
                    onChange={(e) => setOrderNum(Number(e.target.value))}
                    sx={{mb: 2}}
                    slotProps={{
                        htmlInput: {
                            min: 1,
                        },
                    }}
                />

                <Typography sx={{mb: 1}}>Task Content</Typography>

                {mode === "RICH" ? (
                    <Box sx={ckeditorSx}>
                        <CKEditor
                            editor={ClassicEditor as any}
                            data={content}
                            onChange={(_event: any, editor: any) => {
                                const html = editor.getData();
                                setContent(html);
                            }}
                            config={{mediaEmbed: {previewsInData: true}}}
                        />
                    </Box>
                ) : (
                    <TextField
                        fullWidth
                        multiline
                        minRows={14}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder={`Paste pure HTML/CSS here (example):
<div style="border:1px solid #ddd;padding:16px">
  <h3>Hello</h3>
  <style>
    .box { color: red; }
  </style>
</div>`}
                        sx={{mb: 2, ...codeTextAreaSx}}
                        slotProps={{
                            input: {
                                spellCheck: false,
                            },
                        }}
                    />
                )}

                <Box sx={{display: "flex", gap: 2}}>
                    <Button variant="contained" onClick={submit} disabled={saving}>
                        {saving ? "Saving..." : "Create"}
                    </Button>

                    <Button variant="outlined" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                </Box>
            </AppModalBox>
        </Modal>
    );
};

export default AddPracticeTaskModal;